import React, { useState } from "react";
import {
  PiLockKeyLight,
  PiRocketLaunchLight,
  PiArrowRightLight,
  PiShieldCheckLight,
  PiCrownLight,
  PiPuzzlePieceLight,
  PiEnvelopeSimpleLight,
  PiCheckCircleLight,
  PiPlusCircleLight,
} from "react-icons/pi";
import Button from "../../shared/TailwindComponents/Button";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { requestFeatureFromAdmin, addAddon, LicensePlan } from "../../../store/actions/PlanActions";

interface LicenseGateModalProps {
  /** Human-readable section/feature label */
  title: string;
  /** Explanation of what's needed — can include HTML */
  description: string;
  /** Name of the plan or addon that unlocks this feature */
  planName?: string;
  /** "base" or "addon" */
  category?: string;
  /** The section or feature key being gated (e.g. 'finance', 'review_ai') */
  section?: string;
  onViewPlans: () => void;
  onClose: () => void;
}

export default function LicenseGateModal({
  title,
  description,
  planName,
  category,
  section,
  onViewPlans,
  onClose,
}: LicenseGateModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isAddon = category === "addon";
  const isOrgAdmin = useSelector(
    (state: RootState) => state.user.userInfo?.is_org_admin === true
  );
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const matchingAddon = useSelector((state: RootState) =>
    isAddon && planName
      ? (state.license?.addons ?? []).find((a: LicensePlan) => a.display_name === planName)
      : null
  );
  const [requestSent, setRequestSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [addonLoading, setAddonLoading] = useState(false);

  const handleRequestFromAdmin = async () => {
    if (!section || sending) return;
    setSending(true);
    const success = await dispatch(requestFeatureFromAdmin(section));
    setSending(false);
    if (success) {
      setRequestSent(true);
    }
  };

  const handleAddAddon = async () => {
    if (!matchingAddon || addonLoading) return;
    const orgId = userInfo?.OrganizationID || userInfo?.organization_id;
    const email = userInfo?.Email || userInfo?.email || "";
    if (!orgId) return;

    setAddonLoading(true);
    try {
      const successUrl = `${window.location.origin}/account?tab=plans&success=true`;
      const cancelUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
      const result = await dispatch(
        addAddon({
          addon_license_id: matchingAddon.id,
          billing_period: "monthly",
          seat_count: 1,
          email,
          success_url: successUrl,
          cancel_url: cancelUrl,
        })
      );
      if (result?.requires_checkout && result?.url) {
        window.location.assign(result.url);
        return;
      }
      // Addon was added directly (org had Stripe sub) — close modal
      if (result) {
        onClose();
        window.location.reload();
      }
    } finally {
      setAddonLoading(false);
    }
  };

  return (
    <div className="px-6 py-4">
      {/* Icon + heading */}
      <div className="flex flex-col items-center text-center mb-6">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-warning-50 dark:bg-warning-900/30 mb-4">
          <PiLockKeyLight className="w-7 h-7 text-warning-600 dark:text-warning-400" />
        </div>
        <p
          className="text-sm text-gray-600 dark:text-gray-300 max-w-sm"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>

      {/* Recommended plan card */}
      {planName && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-gray-700 shadow-sm">
              {isAddon ? (
                <PiPuzzlePieceLight className="w-5 h-5 text-gray-900 dark:text-gray-100" />
              ) : (
                <PiCrownLight className="w-5 h-5 text-gray-900 dark:text-gray-100" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {planName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isAddon
                  ? t("Add this to your current plan")
                  : t("Upgrade your base plan")}
              </p>
            </div>
            <PiShieldCheckLight className="w-5 h-5 text-success-600 dark:text-success-400" />
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button variant="neutral" size="small" onClick={onClose} className="flex-1">
          {t("Maybe Later")}
        </Button>
        {isOrgAdmin ? (
          isAddon && matchingAddon ? (
            <div className="flex items-center gap-2 flex-1">
              <Button
                variant="primary"
                size="small"
                onClick={handleAddAddon}
                disabled={addonLoading}
                className="flex-1"
              >
                <PiPlusCircleLight className="w-4 h-4 mr-1.5" />
                {addonLoading ? t("Loading...") : t("Add Add-on")}
              </Button>
              <Button
                variant="neutral"
                size="small"
                onClick={() => {
                  onClose();
                  onViewPlans();
                }}
                title={t("View all plans")}
              >
                <PiArrowRightLight className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="primary"
              size="small"
              onClick={() => {
                onClose();
                onViewPlans();
              }}
              className="flex-1"
            >
              <PiRocketLaunchLight className="w-4 h-4 mr-1.5" />
              {t("View Plans")}
              <PiArrowRightLight className="w-4 h-4 ml-1.5" />
            </Button>
          )
        ) : requestSent ? (
          <Button variant="primary" size="small" disabled className="flex-1">
            <PiCheckCircleLight className="w-4 h-4 mr-1.5" />
            {t("Request Sent")}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="small"
            onClick={handleRequestFromAdmin}
            disabled={sending}
            className="flex-1"
          >
            <PiEnvelopeSimpleLight className="w-4 h-4 mr-1.5" />
            {sending ? t("Sending...") : t("Request from Admin")}
          </Button>
        )}
      </div>
    </div>
  );
}
